import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  getOsaTimeRanges,
  isOsaTimeRangeKey,
} from '@/features/scout-scheduling/utils/timeRange.utils'
import { useScoutsQuery } from '@/features/scouts/api'
import type { Scout } from '@/features/scouts/types'

// Constants
const SCOUTS_LIMIT = 50
const DEFAULT_HOUR = 9
const DEFAULT_MINUTES = 0
const WORK_HOURS = { MIN: 6, MAX: 20 }
const TIME_INTERVAL_MINUTES = 15

// Helper functions
const createInitialDate = (dateString: string): Date => {
  const date = new Date(dateString)
  date.setHours(DEFAULT_HOUR, DEFAULT_MINUTES, 0, 0)
  return date
}

const formatTimeString = (hour: number, minute: number): string =>
  `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

const isSameDate = (date1: Date, date2: Date): boolean =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate()

interface ManualScheduleDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (scout: Scout, dateTime: string) => void
  customerName?: string
  proposedDates?: (string | null)[]
  proposedTimeRanges?: (string | null)[]
}

export const ManualScheduleDialog = ({
  open,
  onClose,
  onConfirm,
  customerName,
  proposedDates = [],
  proposedTimeRanges = [],
}: ManualScheduleDialogProps) => {
  const [selectedScout, setSelectedScout] = useState<Scout | null>(null)
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [datePickerError, setDatePickerError] = useState<string | null>(null)

  // API: Fetch scouts with search query when dialog is open
  const {
    data: scoutsData,
    isLoading: scoutsLoading,
    error: scoutsError,
  } = useScoutsQuery(
    {
      limit: SCOUTS_LIMIT,
      q: searchQuery || undefined,
    },
    { enabled: open }
  )

  // Data: Memoized scouts list
  const scouts = useMemo((): Scout[] => (scoutsData?.data as Scout[]) || [], [scoutsData])

  // Date validation: Disable past dates
  const shouldDisableDate = useCallback((date: Date | null) => {
    if (!date) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }, [])

  // Validation: Check if selected date matches proposed dates
  const isDateWithinProposed = useMemo(() => {
    if (!selectedDateTime || proposedDates.length === 0) return true
    if (isNaN(selectedDateTime.getTime())) return true

    return proposedDates.some(proposedDate => {
      if (!proposedDate) return false
      const proposedDateObj = new Date(proposedDate)
      return isSameDate(selectedDateTime, proposedDateObj)
    })
  }, [selectedDateTime, proposedDates])

  // Time validation: Restrict hours and minutes
  const shouldDisableTime = useCallback((value: Date, view: 'hours' | 'minutes' | 'seconds') => {
    if (view === 'hours') {
      const hour = value.getHours()
      return hour < WORK_HOURS.MIN || hour > WORK_HOURS.MAX
    }
    if (view === 'minutes') {
      const minutes = value.getMinutes()
      return minutes % TIME_INTERVAL_MINUTES !== 0
    }
    if (view === 'seconds') {
      return value.getSeconds() !== 0
    }
    return false
  }, [])

  // Effect: Preselect first proposed date when dialog opens
  useEffect(() => {
    if (open && proposedDates.length > 0 && !selectedDateTime) {
      const firstProposedDate = proposedDates.find(date => date !== null)
      if (firstProposedDate) {
        const preselectedDate = createInitialDate(firstProposedDate)
        setSelectedDateTime(preselectedDate)
      }
    }
  }, [open, proposedDates, selectedDateTime])

  // Validation: Check if selected time is within proposed ranges
  const isTimeWithinRange = useMemo(() => {
    if (!selectedDateTime || proposedDates.length === 0) return true
    if (isNaN(selectedDateTime.getTime())) return true

    const selectedDateString = selectedDateTime.toISOString().split('T')[0]
    const selectedTimeString = formatTimeString(
      selectedDateTime.getHours(),
      selectedDateTime.getMinutes()
    )

    // Find matching proposed date and validate time range
    for (let i = 0; i < proposedDates.length; i++) {
      const proposedDate = proposedDates[i]
      const proposedTimeRangeKey = proposedTimeRanges[i]

      if (proposedDate && proposedTimeRangeKey) {
        const proposedDateString = new Date(proposedDate).toISOString().split('T')[0]

        if (selectedDateString === proposedDateString && isOsaTimeRangeKey(proposedTimeRangeKey)) {
          const { minTime, maxTime } = getOsaTimeRanges(new Date(proposedDate))[
            proposedTimeRangeKey
          ]
          return selectedTimeString >= minTime && selectedTimeString < maxTime
        }
      }
    }

    return true
  }, [selectedDateTime, proposedDates, proposedTimeRanges])

  // Handlers
  const handleSubmit = () => {
    if (selectedScout && selectedDateTime) {
      onConfirm(selectedScout, selectedDateTime.toISOString())
      handleClose()
    }
  }

  const handleClose = () => {
    // Reset all form state
    setSelectedScout(null)
    setSelectedDateTime(null)
    setSearchQuery('')
    setDatePickerError(null)
    onClose()
  }

  // Form state
  const isSubmitDisabled = !selectedScout || !selectedDateTime || !!datePickerError

  // Error messages
  const getDatePickerHelperText = () => {
    switch (datePickerError) {
      case 'shouldDisableDate':
        return 'Bitte wählen Sie ein zukünftiges Datum'
      case 'invalidDate':
        return 'Ungültiges Datum'
      case 'shouldDisableTime-hours':
        return `Bitte wählen Sie eine Stunde zwischen ${WORK_HOURS.MIN}:00 und ${WORK_HOURS.MAX}:00`
      case 'shouldDisableTime-minutes':
        return `Bitte wählen Sie Minuten in ${TIME_INTERVAL_MINUTES}-Minuten-Intervallen (z.B. 00, 15, 30, 45)`
      default:
        return datePickerError
    }
  }

  // Date picker: Check if date is proposed
  const isProposedDate = useCallback(
    (date: Date) => {
      if (proposedDates.length === 0) return false

      return proposedDates.some(proposedDate => {
        if (!proposedDate) return false
        const proposedDateObj = new Date(proposedDate)
        return isSameDate(date, proposedDateObj)
      })
    },
    [proposedDates]
  )

  // Date picker: Custom day renderer with proposed date styling
  const CustomDay = (props: PickersDayProps) => {
    const isProposed = props.day && !isNaN(props.day.getTime()) ? isProposedDate(props.day) : false

    return (
      <PickersDay
        {...props}
        sx={{
          ...(isProposed && {
            textDecoration: 'underline',
            textDecorationColor: 'primary.main',
            textDecorationThickness: '2px',
            textUnderlineOffset: '2px',
            fontWeight: 'bold',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'action.hover',
              textDecoration: 'underline',
              textDecorationColor: 'primary.dark',
              textDecorationThickness: '2px',
            },
            '&.Mui-selected': {
              textDecoration: 'underline',
              textDecorationColor: 'primary.contrastText',
              textDecorationThickness: '2px',
            },
          }),
        }}
      />
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manually Schedule Appointment</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Customer info */}
          {customerName && (
            <Typography variant="body2" color="text.secondary">
              Scheduling appointment for: <strong>{customerName}</strong>
            </Typography>
          )}

          {/* Scout selection */}
          <Autocomplete
            options={scouts}
            value={selectedScout}
            onChange={(_, newValue) => setSelectedScout(newValue)}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            getOptionLabel={option => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            loading={scoutsLoading}
            loadingText="Loading scouts..."
            noOptionsText={scoutsError ? 'Error loading scouts' : 'No scouts found'}
            renderInput={params => (
              <TextField
                {...params}
                label="Search and Select Scout"
                placeholder="Type scout name..."
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {scoutsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                <div>
                  <div>
                    {option.firstName} {option.lastName}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>{option.email}</div>
                </div>
              </li>
            )}
            fullWidth
          />

          {/* Date and time selection */}
          <Stack spacing={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Select Date & Time"
                value={selectedDateTime}
                onChange={setSelectedDateTime}
                onError={setDatePickerError}
                shouldDisableDate={shouldDisableDate}
                shouldDisableTime={shouldDisableTime}
                ampm={false}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: TextField,
                  day: CustomDay,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: getDatePickerHelperText(),
                  },
                  popper: {
                    sx: {
                      '& .MuiClock-clock': {
                        '& .MuiClockNumber-root.Mui-disabled': {
                          display: 'none',
                        },
                      },
                      '& .MuiMultiSectionDigitalClock-root': {
                        '& .MuiMenuItem-root.Mui-disabled': {
                          display: 'none',
                        },
                      },
                      '& .MuiDigitalClock-root': {
                        '& .MuiMenuItem-root.Mui-disabled': {
                          display: 'none',
                        },
                      },
                      '& .MuiDateCalendar-root': {
                        '& .MuiPickersDay-root.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.2)',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            {proposedDates.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1,
                  fontSize: '0.75rem',
                }}
              >
                Unterstrichene Daten sind die vom Kunden vorgeschlagenen Termine
              </Typography>
            )}
          </Stack>

          {/* Validation warnings */}
          {!datePickerError && selectedDateTime && (
            <>
              {!isDateWithinProposed ? (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  Warnung: Das gewählte Datum liegt außerhalb der vorgeschlagenen Termine
                </Typography>
              ) : (
                !isTimeWithinRange && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    Warnung: Die gewählte Zeit liegt außerhalb der Verfügbarkeitszeiten
                  </Typography>
                )
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      {/* Dialog actions */}
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          Schedule Appointment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
